import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, DollarSign, Upload, Save, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useAuth } from '../contexts/AuthContext';
export function CreateEventPage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    type: 'training',
    category: '',
    date: '',
    endDate: '',
    time: '',
    location: '',
    locationType: 'offline',
    maxParticipants: '',
    price: '',
    memberPrice: '',
    description: '',
    requirements: '',
    benefits: '',
    speaker: '',
    certificate: true
  });
  const isChapterLeader = user?.role === 'ketua_chapter' || user?.role === 'ketua_student_chapter';
  const isOrganization = user?.role === 'anggota_organisasi';
  const isCompany = user?.role === 'anggota_badan_usaha';
  const isMitra = user?.role === 'mitra_asosiasi';
  const isEventCoordinator = user?.role === 'koordinator_acara';
  const needsApproval = isChapterLeader || isOrganization || isCompany || isMitra;
  const getOrganizerType = () => {
    if (isChapterLeader) return 'chapter';
    if (isOrganization) return 'organisasi';
    if (isCompany) return 'badan_usaha';
    if (isMitra) return 'mitra';
    return 'komite_pusat';
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const eventData = {
      ...formData,
      organizer: user?.nama || user?.namaOrganisasi,
      organizerType: getOrganizerType(),
      status: needsApproval ? 'pending' : 'approved',
      createdBy: user?.id
    };
    if (needsApproval) {
      alert('Event berhasil diajukan! Menunggu persetujuan Komite Pusat.');
    } else {
      alert('Event berhasil dibuat dan dipublikasikan!');
    }
    navigate('/events');
  };
  return <div className="min-h-screen bg-white" data-id="element-2153">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2154">
        {/* Header */}
        <div className="mb-8" data-id="element-2155">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-id="element-2156">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2157" />
            Kembali
          </Button>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-2158">
            Buat Event Baru
          </h1>
          <p className="text-neutral-600" data-id="element-2159">
            {needsApproval ? 'Event akan direview oleh Komite Pusat sebelum dipublikasikan' : 'Event akan langsung dipublikasikan'}
          </p>
        </div>

        {/* Info Alert */}
        {needsApproval && <Card className="mb-6 bg-atios-light border-atios-primary/20" data-id="element-2160">
            <CardContent className="p-4" data-id="element-2161">
              <div className="flex gap-3" data-id="element-2162">
                <AlertCircle className="w-5 h-5 text-atios-primary flex-shrink-0 mt-0.5" data-id="element-2163" />
                <div className="text-sm text-neutral-700" data-id="element-2164">
                  <p className="font-medium mb-1" data-id="element-2165">Perlu Persetujuan</p>
                  <p data-id="element-2166">
                    Event yang Anda buat akan direview oleh Komite Pusat. Anda
                    akan menerima notifikasi setelah event disetujui atau
                    ditolak.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>}

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2167">
          {/* Basic Information */}
          <Card data-id="element-2168">
            <CardHeader data-id="element-2169">
              <CardTitle data-id="element-2170">Informasi Dasar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2171">
              <Input label="Judul Event" required value={formData.title} onChange={e => setFormData({
              ...formData,
              title: e.target.value
            })} placeholder="Workshop React & Next.js Advanced" data-id="element-2172" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2173">
                <Select label="Jenis Event" required value={formData.type} onChange={e => setFormData({
                ...formData,
                type: e.target.value
              })} data-id="element-2174">
                  <option value="training" data-id="element-2175">Training</option>
                  <option value="seminar" data-id="element-2176">Seminar</option>
                  <option value="workshop" data-id="element-2177">Workshop</option>
                  <option value="webinar" data-id="element-2178">Webinar</option>
                  <option value="meeting" data-id="element-2179">Meeting</option>
                  <option value="conference" data-id="element-2180">Conference</option>
                </Select>

                <Select label="Kategori" required value={formData.category} onChange={e => setFormData({
                ...formData,
                category: e.target.value
              })} data-id="element-2181">
                  <option value="" data-id="element-2182">Pilih Kategori</option>
                  <option value="teknologi" data-id="element-2183">Teknologi</option>
                  <option value="bisnis" data-id="element-2184">Bisnis</option>
                  <option value="networking" data-id="element-2185">Networking</option>
                  <option value="pengembangan_karir" data-id="element-2186">Pengembangan Karir</option>
                  <option value="sertifikasi" data-id="element-2187">Sertifikasi</option>
                </Select>
              </div>

              <div data-id="element-2188">
                <label className="text-sm font-medium text-neutral-900 block mb-2" data-id="element-2189">
                  Deskripsi Event
                </label>
                <textarea required value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full min-h-[120px] rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atios-primary" placeholder="Jelaskan tentang event ini..." data-id="element-2190" />
              </div>
            </CardContent>
          </Card>

          {/* Date & Location */}
          <Card data-id="element-2191">
            <CardHeader data-id="element-2192">
              <CardTitle data-id="element-2193">Tanggal & Lokasi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2194">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2195">
                <Input label="Tanggal Mulai" type="date" required value={formData.date} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} data-id="element-2196" />
                <Input label="Tanggal Selesai" type="date" required value={formData.endDate} onChange={e => setFormData({
                ...formData,
                endDate: e.target.value
              })} data-id="element-2197" />
                <Input label="Waktu" type="time" required value={formData.time} onChange={e => setFormData({
                ...formData,
                time: e.target.value
              })} data-id="element-2198" />
              </div>

              <Select label="Tipe Lokasi" required value={formData.locationType} onChange={e => setFormData({
              ...formData,
              locationType: e.target.value
            })} data-id="element-2199">
                <option value="offline" data-id="element-2200">Offline</option>
                <option value="online" data-id="element-2201">Online</option>
                <option value="hybrid" data-id="element-2202">Hybrid</option>
              </Select>

              <Input label="Lokasi" required value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} placeholder={formData.locationType === 'online' ? 'Link Zoom/Google Meet' : 'Alamat lengkap venue'} data-id="element-2203" />
            </CardContent>
          </Card>

          {/* Participants & Pricing */}
          <Card data-id="element-2204">
            <CardHeader data-id="element-2205">
              <CardTitle data-id="element-2206">Peserta & Harga</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2207">
              <Input label="Maksimal Peserta" type="number" required value={formData.maxParticipants} onChange={e => setFormData({
              ...formData,
              maxParticipants: e.target.value
            })} placeholder="50" data-id="element-2208" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2209">
                <Input label="Harga Umum (Rp)" type="number" required value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} placeholder="500000" data-id="element-2210" />
                <Input label="Harga Anggota (Rp)" type="number" required value={formData.memberPrice} onChange={e => setFormData({
                ...formData,
                memberPrice: e.target.value
              })} placeholder="250000" data-id="element-2211" />
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card data-id="element-2212">
            <CardHeader data-id="element-2213">
              <CardTitle data-id="element-2214">Informasi Tambahan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2215">
              <Input label="Pembicara/Trainer" value={formData.speaker} onChange={e => setFormData({
              ...formData,
              speaker: e.target.value
            })} placeholder="Dr. John Doe, Expert in React" data-id="element-2216" />

              <div data-id="element-2217">
                <label className="text-sm font-medium text-neutral-900 block mb-2" data-id="element-2218">
                  Persyaratan
                </label>
                <textarea value={formData.requirements} onChange={e => setFormData({
                ...formData,
                requirements: e.target.value
              })} className="w-full min-h-[80px] rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atios-primary" placeholder="Laptop, basic knowledge of JavaScript, dll" data-id="element-2219" />
              </div>

              <div data-id="element-2220">
                <label className="text-sm font-medium text-neutral-900 block mb-2" data-id="element-2221">
                  Manfaat yang Didapat
                </label>
                <textarea value={formData.benefits} onChange={e => setFormData({
                ...formData,
                benefits: e.target.value
              })} className="w-full min-h-[80px] rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atios-primary" placeholder="Sertifikat, materi, networking, dll" data-id="element-2222" />
              </div>

              <div className="flex items-center gap-2" data-id="element-2223">
                <input type="checkbox" id="certificate" checked={formData.certificate} onChange={e => setFormData({
                ...formData,
                certificate: e.target.checked
              })} className="h-4 w-4 rounded border-neutral-300 text-atios-primary focus:ring-atios-primary" data-id="element-2224" />
                <label htmlFor="certificate" className="text-sm text-neutral-700" data-id="element-2225">
                  Event ini memberikan sertifikat
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4" data-id="element-2226">
            <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="flex-1" data-id="element-2227">
              Batal
            </Button>
            <Button type="submit" className="flex-1" data-id="element-2228">
              <Save className="w-4 h-4 mr-2" data-id="element-2229" />
              {needsApproval ? 'Ajukan Event' : 'Publikasikan Event'}
            </Button>
          </div>
        </form>
      </div>
    </div>;
}